import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import Moment from 'moment';
import {
  AppCard,
  AppDatePicker,
  AppDeleteModal,
  AppInput,
  AppNoInfo,
  AppNotificationAlert,
  AppRemixIcon,
} from "@/layout/AppComponents";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { axiosPath, path } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import AppSubmitButton from "../../../../layout/components/form/AppSubmitButton";
import errorHandler from "@/layout/components/error/ErrorHandler";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import ModuleComsaEnvironmentalTitleCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalOffice/ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard";
import ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsAcquisitionPhaseCard from "../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalOffice/ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsAcquisitionPhaseCard";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";
import _ from "lodash";

export default function ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsScreen() {
  const [deleteSectionPassword] = useState("mjKr3@i4");
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();
  const [showModalDeleteSectionModal, setShowModalDeleteSectionModal] =
    useState();
  const [electricityPreventiveMeasureOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="under.study" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="acquisition.less.percent.green.energy"
        />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="between.percent.green.energy"
        />
      ),
      value: 2,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="more.percent.green.energy.contracted"
        />
      ),
      value: 3,
    },
  ]);
  const [paperPerPersonPreventiveMeasureOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="less.percent.eco.label"
        />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="less.percent.recycled.paper"
        />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="between.percent.recycled.paper"
        />
      ),
      value: 2,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="more.percent.recycled.paper"
        />
      ),
      value: 3,
    },
  ]);
  const [gasOilPreventiveMeasureOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator file="module-comsaEnvironmental" name="under.study" />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="acquisition.less.percent.lpg.electric"
        />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="between.percent.lpg.electric"
        />
      ),
      value: 2,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="more.percent.lpg.electric"
        />
      ),
      value: 3,
    },
  ]);
  const [otherPreventiveMeasureOptions] = useState([
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="no.study.eco.alternatives"
        />
      ),
      value: 0,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="acquisition.less.percent.eco.quantities"
        />
      ),
      value: 1,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="between.percent.eco.quantities"
        />
      ),
      value: 2,
    },
    {
      label: ReactDOMServer.renderToString(
        <Translator
          file="module-comsaEnvironmental"
          name="more.percent.eco.quantities.contracted"
        />
      ),
      value: 3,
    },
  ]);

  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "normalEnvironmentalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(
              r.data?.sheetData ?? {
                releaseDate: null,
                review: "",
                normalEnvironmentalAspectsTable: [],
              }
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "normalEnvironmentalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  const handleDeleteSection = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_delete"),
          { moduleBridgeHex, subModuleBridgeHex, plantHex }
        )
        .then((r) => {
          dispatch(
            currentPageAction.change("moduleData", r.data?.moduleData, true)
          );
          navigate(
            path({
              name: "user_module_comsaEnvironmental_home",
              params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
            })
          );
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  const getQuartile = useCallback((values, setFieldValue) => {
    const filteredValues = Object.keys(values)
      .filter(
        (key) =>
          ![
            "releaseDate",
            "review",
            "normalEnvironmentalAspectsTable",
          ].includes(key)
      )
      .reduce((obj, key) => {
        obj[key] = values[key];
        return obj;
      }, {});

    const updatedEvaluation = {};

    _.mapValues(filteredValues, (v, index) => {
      if (_.isNumber(v?.relativeTotalScore)) {
        updatedEvaluation[index] = v?.relativeTotalScore;
      }
    });

    function getLibreOfficeQuartile(values2, quartile) {
      const sorted = [...values2].sort((a, b) => a - b);
      const n = sorted.length;
      if (n === 0) return null;

      const p = (n - 1) * (quartile / 4);
      const lower = Math.floor(p);
      const upper = Math.ceil(p);
      const weight = p % 1;

      if (lower === upper) return sorted[lower];
      return sorted[lower] * (1 - weight) + sorted[upper] * weight;
    }

    function marcarMayoresQueQ3(obj) {
      const values3 = Object.values(obj);
      const q3 = getLibreOfficeQuartile(values3, 3);

      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = value > q3 ? true : value;
      }

      return result;
    }

    const finalUpdatedEvaluation = marcarMayoresQueQ3(updatedEvaluation);

    if (!_.isEqual(values?.evaluation, finalUpdatedEvaluation)) {
      setFieldValue("evaluation", finalUpdatedEvaluation);
    }
  }, []);

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="office"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_office_normal_environmental_aspects" />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator
                file="module-comsaEnvironmental"
                name="office.environmental.aspects.identification.evaluation"
              />
            </Title>
            <Subtitle className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="office.normal.environmental.aspects"
              />
            </Subtitle>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({
              setFieldValue,
              handleSubmit,
              values,
              errors,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <AppCard
                  header={
                    <>
                      <span
                        className="btn btn-danger btn-xs float-right"
                        onClick={() => setShowModalDeleteSectionModal(true)}
                      >
                        <AppRemixIcon icon="delete-bin-6" />
                      </span>
                    </>
                  }
                  className="mb-2"
                >
                  <div className="row">
                    <div className="col-lg-6">
                      <AppDatePicker
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaEnvironmental"
                            name="release.date"
                          />
                        )}
                        value={
                          values.releaseDate
                            ? Moment.unix(values.releaseDate).format(
                                "DD/MM/YYYY"
                              )
                            : ""
                        }
                        error={errors?.releaseDate}
                        onChange={(date) =>
                          setFieldValue(
                            "releaseDate",
                            date ? Moment(date, "DD/MM/YYYY").unix() : ""
                          )
                        }
                        placeholder="dd/mm/yyyy"
                        isClearable
                        showMonthDropdown
                        showYearDropdown
                      />
                    </div>
                    <div className="col-lg-6">
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaEnvironmental"
                            name="review"
                          />
                        )}
                        onChange={(value) => setFieldValue("review", value)}
                        value={values.review}
                        error={errors.review}
                        isClearable
                      />
                    </div>
                    <div className="col-lg-12">
                      <div>
                        <small>
                          <Translator
                            file="module-comsaEnvironmental"
                            name="office.applicability.periodic.review"
                          />
                        </small>
                      </div>
                    </div>
                  </div>
                </AppCard>
                <ModuleComsaEnvironmentalTitleCard color="info">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="phase"
                        />
                      )}
                    >
                      <Translator file="module-comsaEnvironmental" name="uso" />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.liquid.waste"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.sanitaryOriginWaters}
                  evaluation={values?.evaluation}
                  section="sanitaryOriginWaters"
                  setFieldValue={(n, v) =>
                    setFieldValue(`sanitaryOriginWaters.${n}`, v)
                  }
                  motive={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="sanitary.origin.waters"
                    />
                  )}
                  environmentalAffection={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="sewer.pollution"
                    />
                  )}
                  noApplyQuantity
                  noApplyCorrectiveMeasures
                />
                {getQuartile(values, setFieldValue)}
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="phase"
                        />
                      )}
                    >
                      <Translator file="module-comsaEnvironmental" name="uso" />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="consumption.natural.resources"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.electricity}
                  evaluation={values?.evaluation}
                  section="electricity"
                  setFieldValue={(n, v) => setFieldValue(`electricity.${n}`, v)}
                  motive={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="electricity.per.square.meter"
                    />
                  )}
                  environmentalAffection={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="global.warming"
                    />
                  )}
                  noApplyToxicity
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.waterPerPerson}
                  evaluation={values?.evaluation}
                  section="waterPerPerson"
                  setFieldValue={(n, v) =>
                    setFieldValue(`waterPerPerson.${n}`, v)
                  }
                  motive={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="water.per.person.recommendation"
                    />
                  )}
                  environmentalAffection={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="water.stress.demand.exceeds.availability"
                    />
                  )}
                  noApplyToxicity
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.paperPerPerson}
                  evaluation={values?.evaluation}
                  section="paperPerPerson"
                  setFieldValue={(n, v) =>
                    setFieldValue(`paperPerPerson.${n}`, v)
                  }
                  motive={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="paper.per.person"
                    />
                  )}
                  environmentalAffection={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.generation"
                    />
                  )}
                  noApplyToxicity
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.gas}
                  evaluation={values?.evaluation}
                  section="gas"
                  setFieldValue={(n, v) => setFieldValue(`gas.${n}`, v)}
                  motive={ReactDOMServer.renderToString(
                    <Translator file="module-comsaEnvironmental" name="gas" />
                  )}
                  environmentalAffection={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="resource.depletion.air.pollution.reduction"
                    />
                  )}
                  noApplyToxicity
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.gasOil}
                  evaluation={values?.evaluation}
                  section="gasOil"
                  setFieldValue={(n, v) => setFieldValue(`gasOil.${n}`, v)}
                  motive={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="diesel"
                    />
                  )}
                  environmentalAffection={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="resource.depletion.air.pollution.reduction"
                    />
                  )}
                  noApplyToxicity
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="phase"
                        />
                      )}
                    >
                      <Translator file="module-comsaEnvironmental" name="uso" />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="emissions.atmospheric.emissions"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.cauldronAirConditioning}
                  evaluation={values?.evaluation}
                  section="cauldronAirConditioning"
                  setFieldValue={(n, v) =>
                    setFieldValue(`cauldronAirConditioning.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="boilers.air.conditioning"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="air.pollution"
                    />
                  }
                  noApplyQuantity
                  noApplyCorrectiveMeasures
                />
                <ModuleComsaEnvironmentalTitleCard color="warning">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="phase"
                        />
                      )}
                    >
                      <Translator file="module-comsaEnvironmental" name="uso" />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="emissions.noise"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.airConditioning}
                  evaluation={values?.evaluation}
                  section="airConditioning"
                  setFieldValue={(n, v) =>
                    setFieldValue(`airConditioning.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="air.conditioning"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="nuisance.neighbours.environment"
                    />
                  }
                  noApplyQuantity
                  noApplyCorrectiveMeasures
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="phase"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="disposal"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.non.hazardous.waste"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.paperCardboard}
                  evaluation={values?.evaluation}
                  section="paperCardboard"
                  setFieldValue={(n, v) =>
                    setFieldValue(`paperCardboard.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="paper.cardboard"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.plasticWaste}
                  evaluation={values?.evaluation}
                  section="plasticWaste"
                  setFieldValue={(n, v) =>
                    setFieldValue(`plasticWaste.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="plastic.waste"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.glass}
                  evaluation={values?.evaluation}
                  section="glass"
                  setFieldValue={(n, v) => setFieldValue(`glass.${n}`, v)}
                  motive={
                    <Translator file="module-comsaEnvironmental" name="glass" />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.organic}
                  evaluation={values?.evaluation}
                  section="organic"
                  setFieldValue={(n, v) => setFieldValue(`organic.${n}`, v)}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="organic"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.noDangerousWastesToner}
                  evaluation={values?.evaluation}
                  section="noDangerousWastesToner"
                  setFieldValue={(n, v) =>
                    setFieldValue(`noDangerousWastesToner.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="toners"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.computers}
                  evaluation={values?.evaluation}
                  section="computers"
                  setFieldValue={(n, v) => setFieldValue(`computers.${n}`, v)}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="computer.equipment"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />

                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.mobilephones}
                  evaluation={values?.evaluation}
                  section="mobilephones"
                  setFieldValue={(n, v) =>
                    setFieldValue(`mobilephones.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="mobile.phones"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="territorial.occupation"
                    />
                  }
                />
                <ModuleComsaEnvironmentalTitleCard color="secondary">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="phase"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="disposal"
                      />
                    </span>{" "}
                    /{" "}
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="waste.bar.hazardous.waste"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.battery}
                  evaluation={values?.evaluation}
                  section="battery"
                  setFieldValue={(n, v) => setFieldValue(`battery.${n}`, v)}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="batteries"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="soil.pollution"
                    />
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.fluorescent}
                  evaluation={values?.evaluation}
                  section="fluorescent"
                  setFieldValue={(n, v) => setFieldValue(`fluorescent.${n}`, v)}
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="fluorescent.lamps"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="soil.pollution"
                    />
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsCard
                  values={values.dangerousWastesToner}
                  evaluation={values?.evaluation}
                  section="dangerousWastesToner"
                  setFieldValue={(n, v) =>
                    setFieldValue(`dangerousWastesToner.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="toners"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="soil.pollution"
                    />
                  }
                />
                <h5 className="mt-4 mb-4 text-center">
                  <Translator
                    file="module-comsaEnvironmental"
                    name="acquisition.phase"
                  />
                </h5>
                <ModuleComsaEnvironmentalTitleCard color="success">
                  <h6 className="mb-0">
                    <span
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaEnvironmental"
                          name="aspect"
                        />
                      )}
                    >
                      <Translator
                        file="module-comsaEnvironmental"
                        name="acquisition.natural.resources.consumption"
                      />
                    </span>
                  </h6>
                </ModuleComsaEnvironmentalTitleCard>
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsAcquisitionPhaseCard
                  values={values?.acquisitionPhase?.electricity}
                  setFieldValue={(n, v) =>
                    setFieldValue(`acquisitionPhase.electricity.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="electricity.per.square.meter"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="global.warming"
                    />
                  }
                  preventiveMeasureOptions={electricityPreventiveMeasureOptions}
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsAcquisitionPhaseCard
                  values={values?.acquisitionPhase?.paperPerPerson}
                  setFieldValue={(n, v) =>
                    setFieldValue(`acquisitionPhase.paperPerPerson.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="paper.per.person"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="waste.generation"
                    />
                  }
                  preventiveMeasureOptions={
                    paperPerPersonPreventiveMeasureOptions
                  }
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsAcquisitionPhaseCard
                  values={values?.acquisitionPhase?.gasOil}
                  setFieldValue={(n, v) =>
                    setFieldValue(`acquisitionPhase.gasOil.${n}`, v)
                  }
                  motive={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="diesel"
                    />
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="resource.depletion.air.pollution.reduction"
                    />
                  }
                  preventiveMeasureOptions={gasOilPreventiveMeasureOptions}
                />
                <ModuleComsaEnvironmentalOfficeNormalEnvironmentalAspectsAcquisitionPhaseCard
                  values={values?.acquisitionPhase?.other}
                  setFieldValue={(n, v) =>
                    setFieldValue(`acquisitionPhase.other.${n}`, v)
                  }
                  environmentalAffection={
                    <Translator
                      file="module-comsaEnvironmental"
                      name="global.warming"
                    />
                  }
                  preventiveMeasureOptions={otherPreventiveMeasureOptions}
                />
                <div className="mb-5" />
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
      <AppDeleteModal
        show={showModalDeleteSectionModal}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-comsaEnvironmental"
            name="office.data.removal"
          />
        )}
        onClose={() => setShowModalDeleteSectionModal(false)}
        onConfirm={handleDeleteSection}
        sensitive
        sensitiveWord={deleteSectionPassword}
        sensitiveText={
          <Translator
            file="module-comsaEnvironmental"
            name="enter.the.password"
          />
        }
        password
      />
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Subtitle = styled.h4`
  text-align: center;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
